
/**
 * productfeatures.config.js
 * Copyright (c) 2025 Quantellia LLC
 *
 * Purpose:
 * This configuration file defines the features available in the application, allowing for modularity and conditional inclusion of functionality.
 * Each feature can have separate client-side and server-side modules, which are dynamically loaded or excluded based on the `enabled` flag.
 *
 * Structure:
 * - Each feature is defined as an object with the following properties:
 *   - `enabled`: A boolean flag indicating whether the feature should be included in the build.
 *   - `clientModules`: An array of paths to client-side JavaScript modules associated with the feature.
 *   - `serverModules`: An array of paths to server-side JavaScript modules associated with the feature.
 * 
 * Why the `noop` is Important:
 * - In cases where a feature is disabled, its associated functions and modules are not included in the build.
 * - The `noop` (short for "no operation") function acts as a safe fallback for such cases. 
 * - When attempting to call a function from a disabled feature, the `noop` ensures that:
 *   1. No runtime errors occur due to missing functions or modules.
 *   2. The application remains stable and functional.
 *   3. Developers can write code that assumes all features exist without needing conditional checks for each call.
 * 
 * Benefits:
 * - Simplifies feature toggling: Features can be enabled or disabled by modifying just one flag in this file.
 * - Supports modular builds: Only the code for enabled features is included, reducing bundle size.
 * - Provides robustness: The `noop` ensures safe execution even when a feature is disabled.
 * 
 * See also:
 * - `featureloader.js` for the dynamic loading and initialization of features based on this configuration.
 * - https://chatgpt.com/c/67761d66-78a8-8006-9929-e696bf62fb4f (under lorien@gmail.com chatgpt account) for a trace of how this approach was designed and developed.
 */

const noop = (...args) => {
  console.warn(`Feature not included in this build. Arguments received:`, args);
};

module.exports = {
  features: {
    // Note that paths should be relative to the "features" directory under "client" and "server" respectively
    helloWorld: {
      enabled: true,
      clientModules: ['./helloworld/helloworld.js'], // Client-specific files
      // serverModules: ['../server/features/helloworld/helloworldserver.js'], // Server-specific files
      serverModules: ['./helloworld/helloworldserver.js'], // Server-specific files
      // serverModules: ['./helloworld/helloworldserver.js'], // Server-specific files
      fallback: noop,
    },
    anotherFeature: {
      enabled: false,
      clientModules: ['./helloworld/anotherfeature.js'],
      serverModules: ['./helloworld/anotherFeatureserver.js'],
      // serverModules: ['../server/features/helloworld/anotherFeatureserver.js'],
      // serverModules: ['./helloworld/anotherFeatureserver.js'],
      fallback: noop,
    },
    // Allows you to A/B test different prompts
    promptAB: {
      enabled: false,
      clientModules: ['./promptab/promptabcore.js'], 
      serverModules: [], // None yet
      fallback: noop,
    },
    // /jumpin and other slash commands from Slack
    jumpin: {
      enabled: false,
      clientModules: ['./jumpin/jumpincore.js'], 
      serverModules: [], // None yet
      fallback: noop,
    },
    // create, edit, save, and otherwise manipulate graphical CDDs
    cddllm: {
      enabled: false,
      clientModules: ['./cddjs/fileformatconverter/qlib.js', './cddjs/fileformatconverter/vismodel.js', './cddjs/cddinodi.js', './cddjs/cddinvis.js', './cddjs/cddtextinit.js', './cddjs/grapher.js','./cddjs/importexport.js'], 
      serverModules: [], // None yet
      fallback: noop,
    }
  },
};

// // Dynamically assign the `invoke` property for each feature
// Object.entries(module.exports.features).forEach(([featureName, featureConfig]) => {
//   featureConfig.invoke = featureConfig.enabled
//     ? (...args) => require(featureConfig.clientModules[0]).default(...args)
//     : noop;
// });
